import React, { Suspense, useState, useEffect, useMemo, useRef } from "react";
import { useParams, useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import ExpandableText from "../../shared/ExpandableText/ExpandableText";
import { Link } from "react-router-dom";
import { User, ArrowUpRight, Share2, Store, Phone, Copy, Check, Users, ArrowLeft, Lock, CreditCard, Camera, Loader2, UtensilsCrossed, Flag, PartyPopper, ClipboardCheck, Hourglass, Pencil, Package, Settings2, Sparkles, CookingPot } from "lucide-react";
import { AppDispatch, RootState } from "../../../dataManagement/uiDb/UiDb";
import { loadProductForEdit, resetToInitialState, updateEssentials } from "../../../dataManagement/uiDb/CreateProductDb";
import { fetchBusinessById } from "../../../services/thunks/BusinessThunk";
import { fetchCaptainSummary } from "../../../services/thunks/OnboardingThunk";
import { canCurate } from "../../../interfaces/OnboardingObject";
import { resolveVisitingCardDefaults } from "../../../services/helpers/VisitingCardHelper";
import ProductApiService from "../../../services/apiServices/ProductApiService";
import { setCurrentBusiness, syncMyBusiness } from "../../../dataManagement/uiDb/BusinessDb";
import {
  getAutopayReadiness,
  ONBOARDING_IN_PROGRESS_LABEL,
  ONBOARDING_IN_PROGRESS_NOTE,
} from "../../../services/helpers/AutopayGateHelper";
import { useBusinessListings } from "../../../hooks/useBusinessListings";
import { useMyBusinesses } from "../../../hooks/useMyBusinesses";
import type { VoiceLaunch } from "../../../interfaces/VoiceObject";
import { useVoiceCapabilities } from "../../../hooks/useVoiceCapabilities";
import { useSubscribeBusiness } from "../../../hooks/useSubscribeBusiness";
// CreateProduct pulls in the full product-authoring flow (image crop,
// pica compression, section/subsection pickers). Lazy so a QR visitor who
// never taps "add product" never downloads it.
const CreateProduct = React.lazy(() => import("../../features/Product/CreateProduct/CreateProduct"));
// Curator-only bulk-upload modal (photo analysis, AI generate, category
// pickers). Lazy for the same reason as CreateProduct -- it's gated behind
// isEditable && isCurator, so almost no visitor ever needs this bundle.
const BusinessBulkUploadActions = React.lazy(() => import("../../features/Business/BusinessTabs/tabs/BusinessBulkUploadActions"));
import { convertProductDetailsToNewProduct } from "../../../services/converters/backendToUi/productConvertor";
import AppConfig from "../../../config/config";
import { useBusinessProfilePicture } from "../../../hooks/useBusinessProfilePicture";
import { useBusinessLogoUpload } from "../../../hooks/useBusinessLogoUpload";
import { useBusinessBrandColor } from "../../../hooks/useBusinessBrandColor";
import { useBusinessConfig } from "../../../hooks/useBusinessConfig";
import { useBusinessGallery } from "../../../hooks/useBusinessGallery";
import { useBusinessBannerUpload } from "../../../hooks/useBusinessBannerUpload";
import { usePullToStretchBanner } from "../../../hooks/usePullToStretchBanner";
import { useBusinessHeaderScroll } from "../../../hooks/useBusinessHeaderScroll";
import BusinessLogoUploader from "../../features/Business/CreateBusinessModal/childComponents/BusinessLogoUploader";
import BusinessMonogramAvatar from "../../shared/BusinessMonogramAvatar/BusinessMonogramAvatar";
import mediaUtility from "../../../services/utility/MediaUtility";
import { hexToRgba, pastelHex } from "../../../services/utility/BrandColorExtractor";
import { getFontOption } from "../../../constants/BusinessBrandingDefs";
import { ensureGoogleFont } from "../../../services/utility/GoogleFontLoader";
import genericUtility from "../../../services/utility/GenericUtility";
import ShareProfileModal from "../../shared/ShareProfileModal/ShareProfileModal";
import BusinessConfigEditorModal from "../../features/Business/BusinessConfigEditor/BusinessConfigEditorModal";
import BusinessOwnerPill from "../../features/Business/BusinessOwnerPill/BusinessOwnerPill";
import BusinessLiveBadge from "../../features/Business/BusinessLiveBadge";
import BusinessRatingPill from "../../features/Business/BusinessRatingPill";
import BusinessExternalLinks from "../../features/Business/BusinessExternalLinks";
import CustomersModal from "../../features/Business/CustomersModal/CustomersModal";
import CreateBusinessModal from "../../features/Business/CreateBusinessModal/CreateBusinessModal";
import ConfirmOnboardingModal from "../../features/Business/ConfirmOnboardingModal/ConfirmOnboardingModal";
import ConfirmModal from "../../shared/ConfirmModal/ConfirmModal";
import ImageCropModal from "../../shared/ImageCropModal/ImageCropModal";
import BusinessTabBar, { BusinessTab } from "../../features/Business/BusinessTabs/BusinessTabBar";
const ProductsTab = React.lazy(() => import("../../features/Business/BusinessTabs/tabs/ProductsTab"));
const GalleryTab = React.lazy(() => import("../../features/Business/BusinessTabs/tabs/GalleryTab"));
const ReviewsTab = React.lazy(() => import("../../features/Business/BusinessTabs/tabs/ReviewsTab"));
const MoreTab = React.lazy(() => import("../../features/Business/BusinessTabs/tabs/MoreTab"));
const OrdersTab = React.lazy(() => import("../../features/Business/BusinessTabs/tabs/OrdersTab"));
import DineInTab from "../../features/Business/BusinessTabs/tabs/DineInTab";
import { BusinessProfile, BusinessConfigData, BusinessHours } from "../../../interfaces/BusinessObject";
import { ProductDetailsModal } from "../../../interfaces/ProductObject";
import { MediaRecord } from "../../../interfaces/MediaObject";
import { useNotification } from "../../shared/contexts/NotificationContext";
import { NotificationType } from "../../../constants/Enums";
import CreateProductConfigHelper from "../../../services/helpers/CreateProductConfigHelper";
import { setCartBusiness } from "../../../dataManagement/uiDb/CartDb";
import { selectActiveDineInSession } from "../../../dataManagement/uiDb/DineInDb";
import { buildBusinessPath, extractBusinessIdFromParam } from "../../../services/helpers/BusinessSlugHelper";
import FetchErrorState from "../../shared/FetchErrorState";
import ReportContentModal from "../../shared/ReportContentModal/ReportContentModal";
import { isIosWebView } from "../../../services/helpers/platform";
import "./styles/BusinessListings.css";

/** Shared full-page info state (not-found / not-subscribed / owner prompt) —
 *  icon-in-a-circle + heading + description + up to two actions. Keeps the
 *  three "can't show the normal page" states visually consistent. */
interface BusinessInfoStateProps {
  icon: React.ReactNode;
  tone?: "neutral" | "warning";
  title: string;
  description: string;
  errorText?: string | null;
  primaryLabel: string;
  onPrimary: () => void;
  primaryDisabled?: boolean;
  secondaryLabel?: string;
  onSecondary?: () => void;
  tertiaryLabel?: string;
  onTertiary?: () => void;
}

const BusinessInfoState: React.FC<BusinessInfoStateProps> = ({
  icon,
  tone = "neutral",
  title,
  description,
  errorText,
  primaryLabel,
  onPrimary,
  primaryDisabled = false,
  secondaryLabel,
  onSecondary,
  tertiaryLabel,
  onTertiary,
}) => (
  <div className="biz-listings-page">
    <div className="biz-listings-info-state">
      <div className={`biz-listings-info-icon biz-listings-info-icon--${tone}`}>
        {icon}
      </div>
      <h3>{title}</h3>
      <p>{description}</p>
      {errorText && <p className="biz-listings-info-error">{errorText}</p>}
      <div className="biz-listings-info-actions">
        <button
          type="button"
          className="biz-listings-info-btn biz-listings-info-btn--primary"
          onClick={onPrimary}
          disabled={primaryDisabled}
        >
          {primaryLabel}
        </button>
        {secondaryLabel && onSecondary && (
          <button
            type="button"
            className="biz-listings-info-btn biz-listings-info-btn--secondary"
            onClick={onSecondary}
          >
            {secondaryLabel}
          </button>
        )}
      </div>
      {tertiaryLabel && onTertiary && (
        <button
          type="button"
          className="biz-listings-info-btn biz-listings-info-btn--tertiary"
          onClick={onTertiary}
        >
          {tertiaryLabel}
        </button>
      )}
    </div>
  </div>
);

type BusinessFallbackState =
  | "loading"
  | "not-found"
  | "not-subscribed-visitor"
  | "not-subscribed-owner"
  | null;

/** Which (if any) full-page fallback should replace the normal business page.
 *  null means "render normally" (business loaded and subscribed) — computed
 *  once so the fallback conditions can't silently drift apart as more states
 *  get added. "loading" has its own explicit state (rather than being lumped
 *  into null) so the caller never falls through to the real page shell with
 *  half-empty placeholder content while the business is still being fetched.
 *  `canManageSubscription` must be owner/creator only — a collaborator gets the
 *  same visitor fallback as anyone else, since billing isn't their concern.
 *  Takes isBusinessLoading (the business-object fetch only), NOT the page's
 *  combined isLoading — gating on the combined flag meant this could never
 *  resolve until the (unrelated) product-listings fetch also finished, so the
 *  normal page rendered first and only flipped to the fallback once listings
 *  caught up, a visible glitch. */
function resolveBusinessFallbackState(
  isBusinessLoading: boolean,
  business: BusinessProfile | null,
  canManageSubscription: boolean
): BusinessFallbackState {
  if (isBusinessLoading) return "loading";
  if (!business) return "not-found";
  // Fail-closed: an absent/undefined subscriptionActive is treated as NOT
  // subscribed rather than silently letting an unpaid business look active.
  if (!business.subscriptionActive) {
    return canManageSubscription ? "not-subscribed-owner" : "not-subscribed-visitor";
  }
  return null;
}

// TODO(biz-listings-dine-in-badge): badge is hidden for everyone for now.
// Flip to true (or delete this flag) to show it again for dine-in enabled businesses.
const SHOW_DINE_IN_BADGE = false;

const BusinessListings: React.FC = () => {
  const { id: rawParam = "" } = useParams<{ id: string }>();
  // The route param may be a plain businessId (old links) or `<slug>-<id>`
  // (new pretty links) — always resolve to the real UUID before use.
  const businessId = extractBusinessIdFromParam(rawParam);
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch<AppDispatch>();
  const pushedGuardRef = useRef(false);

  const { business, products, totalCount, isLoading, isBusinessLoading, error, loadMore, reload, setProducts } = useBusinessListings(businessId);
  const ratingsSummary = useSelector((s: RootState) => s.business.ratingsSummary);
  const { businesses: myBusinesses } = useMyBusinesses();
  const { subscribe, isSubscribing, getSubscribeError } = useSubscribeBusiness();

  // Scope the collection (cart) to this business.
  // If user enters a different business page, the cart auto-clears.
  useEffect(() => {
    if (businessId) dispatch(setCartBusiness(businessId));
  }, [dispatch, businessId]);
  const { triggerNotification } = useNotification();
  const [createOpen, setCreateOpen] = useState(false);
  // Opens the create popup straight into "Tell Chintu" (create by voice).
  const [startVoice, setStartVoice] = useState(false);
  const [voiceProduct, setVoiceProduct] = useState<VoiceLaunch | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const isBulkUploadOpen = searchParams.get("action") === "bulk-upload";
  const setIsBulkUploadOpen = (open: boolean) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (open) next.set("action", "bulk-upload");
        else next.delete("action");
        return next;
      },
      { replace: true }
    );
  };
  const [isConfirmOnboardingOpen, setIsConfirmOnboardingOpen] = useState(false);
  const [isPhoneCopied, setIsPhoneCopied] = useState(false);

  const handleCopyPhone = (phone: string) => {
    navigator.clipboard?.writeText(phone);
    setIsPhoneCopied(true);
    triggerNotification("Phone number copied to clipboard", NotificationType.SUCCESS);
    setTimeout(() => setIsPhoneCopied(false), 2000);
  };
  const [bannerCropSrc, setBannerCropSrc] = useState<string | null>(null);
  // business.logoUrl is real and reactive (updateBusiness updates Redux's
  // currentBusiness on success), so no local override state is needed — only
  // an onError fallback in case the stored URL fails to load.
  const [logoFailed, setLogoFailed] = useState(false);
  const resolvedLogoUrl = useBusinessProfilePicture(businessId, business?.logoUrl);
  const logoUrl = logoFailed ? null : resolvedLogoUrl;
  const { upload: uploadLogo, isUploading: isLogoUploading } = useBusinessLogoUpload(businessId);

  useEffect(() => {
    // A fresh business (new businessId) shouldn't inherit the previous
    // business's failure history — this component instance is reused across
    // /business/:id -> /business/:otherId navigations.
    setLogoFailed(false);
  }, [businessId]);

  const handleLogoFileSelected = async (file: File) => {
    try {
      await uploadLogo(file);
      setLogoFailed(false);
      triggerNotification("Logo updated!", NotificationType.SUCCESS);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Failed to upload logo.";
      triggerNotification(msg, NotificationType.ERROR);
    }
  };

  const currentUserId = genericUtility.getCookie("userId")?.split("expires")[0] ?? "";

  // Derive role locally from current user vs. owner/collaborators (public endpoints don't return role).
  const myRole = genericUtility.getMyRoleInThisBusiness(
    business?.creatorId,
    business?.ownerId,
    business?.collaborators
  );
  const isOwner = myRole === "owner";
  const isCollaborator = myRole === "collaborator";
  const isAdmin = !!useSelector((s: RootState) => s.profile.data?.admin);
  // Editing the business profile itself (and its logo) stays owner-only; managing
  // listings, gallery and sections is allowed for collaborators too.
  const isEditable = isOwner || isCollaborator;
  // Only owners/collaborators can ever see the voice entry, so anonymous
  // visitors never trigger the capabilities request.
  const voiceCaps = useVoiceCapabilities(isEditable);
  const voiceAvailable = voiceCaps.isSkillAvailable("product.edit");
  const voiceCreateAvailable = voiceCaps.isSkillAvailable("product.create");
  // Warm the voice-create chunk while the owner browses, so "Tell Chintu" opens instantly.
  useEffect(() => {
    if (!isEditable || !voiceCreateAvailable) return;
    const warm = () =>
      void import("../../features/Voice/VoiceCreatePanel/VoiceCreatePanel").catch(() => undefined);
    const idle = (window as Window & { requestIdleCallback?: (cb: () => void) => number }).requestIdleCallback;
    if (idle) idle(warm);
    else setTimeout(warm, 1500);
  }, [isEditable, voiceCreateAvailable]);
  // Global curator role — independent of any business relationship. Bulk
  // "delete all products in this section/sub-section" is gated on isEditable
  // (this business specifically) AND isCurator (the account overall).
  // Deliberately role === CURATOR or BOTH only (canCurate) -- NOT
  // canCurateDisplay's lead bypass. The backend's own curator check
  // (RoleChecker.isCurator) does grant any lead regardless of role, so a
  // lead with role=CAPTAIN could still call the endpoint directly and
  // succeed -- but product intentionally keeps this UI's visibility to the
  // two explicit roles, narrower than what the backend would allow.
  // captainStatus is cached per session by the onboarding slice — reuse it
  // (and only fetch once) rather than re-fetching every time this page
  // mounts. A plain owner/collaborator who has never touched the Captain
  // program gets captainStatus "notCaptain" (or stays "unknown" if the fetch
  // is still in flight) and simply never sees the action — no error is
  // surfaced for that case, it's not one.
  const captainStatus = useSelector((s: RootState) => s.onboarding.captainStatus);
  const captainSummary = useSelector((s: RootState) => s.onboarding.summary);
  const isCaptainSummaryLoading = useSelector(
    (s: RootState) => !!s.onboarding.loadingMap.fetchCaptainSummary
  );
  const isCurator =
    captainStatus === "captain" &&
    !!captainSummary &&
    canCurate(captainSummary.role);
  // Logo and banner may be changed by the owner, a platform admin, or a curator
  // who is also a collaborator of this business (mirrors the backend's
  // updateBusiness rule). A plain collaborator is rejected there, so the
  // controls are hidden from them.
  const canEditMedia = isOwner || isAdmin || (isCollaborator && isCurator);
  // Platform admins get the same bulk-upload access as curators (see the
  // isCurator || isAdmin checks below). Reads the SAME profile object
  // LoadUserData already fetched once before this route rendered (matches
  // AdminProgram.tsx's isAdmin) — no separate profile call needed here.
  useEffect(() => {
    // Business pages are public — a guest can never be a captain, so don't
    // even fire the (guaranteed-401) request for one. The reducer now also
    // resolves an unauthenticated rejection out of "unknown" on its own
    // (see OnboardingDb's fetchCaptainSummary.rejected), but skipping the
    // call here avoids sending it in the first place for the common case.
    if (
      captainStatus === "unknown" &&
      !isCaptainSummaryLoading &&
      genericUtility.isUserLoggedIn()
    ) {
      dispatch(fetchCaptainSummary());
    }
  }, [captainStatus, isCaptainSummaryLoading, dispatch]);

  const handleBack = React.useCallback(() => {
    if (isEditable) {
      navigate("/my-listings");
      return;
    }

    if (location.key === "default") {
      navigate("/home", { replace: true });
    } else {
      navigate(-1);
    }
  }, [isEditable, navigate, location.key]);
  const canEditMoreDetails = isOwner || isCollaborator;
  // Subscription/billing is stricter than isEditable — a collaborator must see
  // the same "not available" fallback a regular visitor sees, not the owner's
  // Subscribe/manage flow. "creator" counts too: effective controller of an
  // orphan business (ownerId === null) — same rule as SubscriptionsProgram.tsx.
  const canManageSubscription = isOwner || myRole === "creator";
  // Reporting is an iOS-only control (App Store UGC-reporting compliance) — it
  // must be reachable to any user, including logged-out reviewers, so it is NOT
  // gated on login. Still hidden when you manage this business yourself
  // (owner/collaborator via isEditable, or creator of an orphan business).
  const canReportBusiness = isIosWebView() && !isEditable && myRole !== "creator";

  const [activeTab, setActiveTab] = useState<BusinessTab>(() => {
    const t = searchParams.get("tab");
    return t === "gallery" ||
      t === "reviews" ||
      t === "more" ||
      t === "orders" ||
      t === "dinein"
      ? (t as BusinessTab)
      : "products";
  });

  // A ?tab= param means someone was deep-linked straight to a tab (dine-in's
  // "View menu" button, NineFindsOrderButton after placing an order, etc.) —
  // scroll past the banner/header once so the (sticky) tab bar and its
  // content are immediately visible instead of requiring a manual scroll
  // every time. Ordinary visits (no ?tab=) land at the top as usual.
  //
  // Why this runs after EVERY render (no dep array) and distrusts the first
  // commit: on SPA navigation from a page that already fetched this business
  // into Redux (dine-in does, for its header), the FIRST commit renders the
  // real tree — anchor included — off that stale business. Then
  // useBusinessListings clears + refetches, tearing that tree down for a
  // "loading" fallback before rebuilding it. A scroll scheduled against that
  // first commit targets a node that's detached by the time it runs —
  // scrollIntoView on a detached node is a silent no-op (the refresh case
  // worked only because Redux starts empty there, so the first commit was
  // the anchor-less loading tree). Hence three defenses: skip commit #1
  // outright, re-read the ref at scroll time instead of capturing a node,
  // and un-mark "done" if the node still turned out to be dead so a later
  // commit retries. The guards make the repeated runs effectively free.
  const tabBarAnchorRef = useRef<HTMLDivElement>(null);
  const scrolledToTabRef = useRef(false);
  const skippedFirstCommitRef = useRef(false);
  useEffect(() => {
    if (scrolledToTabRef.current) return;
    if (!searchParams.get("tab")) {
      // Plain visit — never auto-scroll, and don't keep re-checking.
      scrolledToTabRef.current = true;
      return;
    }
    if (!skippedFirstCommitRef.current) {
      // Commit #1 is either the anchor-less loading tree (refresh) or the
      // doomed stale-business tree (SPA nav) — useless either way, and the
      // clear+refetch guarantees more commits are always coming.
      skippedFirstCommitRef.current = true;
      return;
    }
    if (!tabBarAnchorRef.current) return; // real tree not committed yet — retry next render
    scrolledToTabRef.current = true;
    // One paint cycle so the just-committed layout settles before measuring.
    requestAnimationFrame(() => {
      const anchor = tabBarAnchorRef.current;
      if (!anchor || !anchor.isConnected) {
        // Tree got swapped between commit and paint — retry on the next one.
        scrolledToTabRef.current = false;
        return;
      }
      anchor.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  });
  const [isShareOpen, setIsShareOpen] = useState(false);
  const [isReportOpen, setIsReportOpen] = useState(false);
  const [isCustomersOpen, setIsCustomersOpen] = useState(false);
  const [isEditBizOpen, setIsEditBizOpen] = useState(false);
  const [isConfigEditorOpen, setIsConfigEditorOpen] = useState(false);
  // "Delete all products in this section/sub-section" — armed by ProductsTab's
  // onDeleteGroup callback (see its own doc comment), confirmed via ConfirmModal
  // below. label is the section/sub-section's display name, already run through
  // displaySectionName by the caller, purely for the confirmation copy.
  const [pendingGroupDelete, setPendingGroupDelete] = useState<{
    productIds: string[];
    label: string;
  } | null>(null);
  const [isDeletingGroup, setIsDeletingGroup] = useState(false);

  const handleDeleteGroup = (productIds: string[], label: string) => {
    if (productIds.length === 0) return;
    setPendingGroupDelete({ productIds, label });
  };

  const handleConfirmGroupDelete = async () => {
    if (!pendingGroupDelete || !businessId) return;
    const { productIds } = pendingGroupDelete;
    setIsDeletingGroup(true);
    try {
      await ProductApiService.deleteProductsByBusinessId(businessId, productIds);
      // All-or-nothing on the backend (see deleteProductsByBusinessId's doc
      // comment) — a resolved call means every one of these ids is gone, so a
      // plain filter is safe, no partial-success bookkeeping needed here.
      const deletedIds = new Set(productIds);
      setProducts((prev) => prev.filter((p) => !deletedIds.has(p.productId)));
      triggerNotification(
        `Deleted ${productIds.length} product${productIds.length === 1 ? "" : "s"}.`,
        NotificationType.SUCCESS
      );
    } catch (err) {
      const msg =
        err instanceof Error ? err.message : "Couldn't delete those products. Please try again.";
      triggerNotification(msg, NotificationType.ERROR);
    } finally {
      setIsDeletingGroup(false);
      setPendingGroupDelete(null);
    }
  };

  // Pre-select the business in Redux and open the create modal
  const handleSellFromBusiness = () => {
    dispatch(resetToInitialState());
    if (business) {
      dispatch(updateEssentials({
        businessId: business.businessId,
        listingType: business.listingType as any,
      }));
    }
    setCreateOpen(true);
  };  const handleTellChintu = () => {
    setStartVoice(true);
    handleSellFromBusiness();
  };


  const handleProductVoiceEdit = (productId: string) => {
    const product = products.find((p) => p.productId === productId);
    if (!product) return;
    setVoiceProduct({ skillId: "product.edit", product });
    setCreateOpen(true);
  };

  const handleProductEdit = (productId: string) => {
    dispatch(resetToInitialState());
    setCreateOpen(true);
    const product = products.find((p) => p.productId === productId);
    if (!product) return;
    // Existing media prefills as MediaRecord references (from
    // productMediaResources) — no downloads, no base64, no re-uploads.
    dispatch(loadProductForEdit(convertProductDetailsToNewProduct(product)));
  };

  const [sectionsEnabled, setSectionsEnabled] = useState<boolean>(() => {
    const stored = sessionStorage.getItem(`nine_biz_sections_${businessId}`);
    return stored === null ? true : stored !== "false";
  });

  useEffect(() => {
    const stored = sessionStorage.getItem(`nine_biz_sections_${businessId}`);
    setSectionsEnabled(stored === null ? true : stored !== "false");
  }, [businessId]);

  const handleToggleSections = () => {
    const next = !sectionsEnabled;
    sessionStorage.setItem(`nine_biz_sections_${businessId}`, String(next));
    setSectionsEnabled(next);
  };

  // businessConfig now lives on its own dedicated /config endpoint, not
  // moreDetails — fetched/cached via BusinessConfigDb (see useBusinessConfig).
  const { config: fetchedBusinessConfig } = useBusinessConfig(businessId);
  const businessConfig: BusinessConfigData = fetchedBusinessConfig ?? {};

  // Personalization: an owner-set override always wins; otherwise the accent
  // is sampled from the logo, falling back to a per-listingType default so
  // every business reads as its own space rather than one fixed platform
  // purple. See useBusinessBrandColor / BrandColorExtractor for the logic.
  const { accent, accentDark, tint } = useBusinessBrandColor(
    logoUrl,
    business?.listingType ?? null,
    businessConfig.brandColor?.value ?? null
  );
  // Banner precedence, each tier only ever reached if the one before it is
  // absent or fails to load:
  //   1. A custom banner the owner uploaded — persisted as business.bannerUrl
  //      (via useBusinessBannerUpload, same presigned-storage pipeline as the
  //      gallery). Redux's currentBusiness updates on a successful upload, so
  //      this is reactive — no local override state needed.
  //   2. The first photo already in the business's Gallery (zero extra owner
  //      effort — most businesses have gallery photos before they'd ever
  //      think to set a banner).
  //   3. A generated gradient from the accent color (rendered in the JSX
  //      below when no photo candidate is available/loadable).
  const { upload: uploadBanner, isUploading: isBannerUploading } = useBusinessBannerUpload(businessId);

  const { media: galleryMedia } = useBusinessGallery(businessId);
  const firstGalleryImageUrl = useMemo(() => {
    const firstImage = galleryMedia.find(
      (m) => mediaUtility.kindFromContentType(m.mediaType) === "image"
    );
    return firstImage?.publicUrl ?? null;
  }, [galleryMedia]);

  const bannerCandidates = useMemo(
    () => [business?.bannerUrl, firstGalleryImageUrl].filter((u): u is string => !!u),
    [business?.bannerUrl, firstGalleryImageUrl]
  );
  const [failedBannerUrls, setFailedBannerUrls] = useState<Set<string>>(new Set());
  useEffect(() => {
    // A fresh business (new businessId) shouldn't inherit the previous
    // business's failure history — this component instance is reused across
    // /business/:id -> /business/:otherId navigations.
    setFailedBannerUrls(new Set());
  }, [businessId]);
  const activeBannerUrl = bannerCandidates.find((u) => !failedBannerUrls.has(u)) ?? null;

  const handleBannerFileSelected = async (file: File) => {
    try {
      await uploadBanner(file);
      setFailedBannerUrls(new Set());
      triggerNotification("Banner updated!", NotificationType.SUCCESS);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Failed to upload banner.";
      triggerNotification(msg, NotificationType.ERROR);
    }
  };

  // Pull-to-stretch — only wired up when there's an actual photo to reveal
  // (the generated gradient has no extra detail underneath, so stretching
  // it would just distort a flat color for no reason).
  const bannerRef = useRef<HTMLDivElement>(null);
  const { style: pullStretchStyle, pullRatio: bannerPullRatio } = usePullToStretchBanner(
    bannerRef,
    { enabled: !!activeBannerUrl }
  );

  // Scroll-linked header chrome (fixed top bar fade-in, compact title,
  // banner parallax, sticky tab bar shadow) — writes CSS custom properties
  // straight onto the page root below, never React state, so it can't cause
  // extra renders. pageRef isn't attached yet during the loading/not-found
  // fallback returns further down; the hook's own scroll handler re-checks
  // it on every tick, so it just starts working once the real page mounts.
  const pageRef = useRef<HTMLDivElement>(null);
  useBusinessHeaderScroll(pageRef);

  // Optional display-font override — loads the Google Font only when a
  // business has actually picked one (never a global cost).
  const fontOption = getFontOption(businessConfig.displayFont?.value);
  useEffect(() => {
    ensureGoogleFont(fontOption.googleFontParam);
  }, [fontOption.googleFontParam]);

  const bizStyleVars = {
    "--biz-accent": accent,
    "--biz-accent-dark": accentDark,
    "--biz-tint": tint,
    // Softer fill + a slightly stronger border than --biz-tint — used by
    // header badges (type badge) so the edge reads clearly without the fill
    // itself looking like a solid block next to lighter status pills.
    "--biz-badge-bg": hexToRgba(accent, 0.08),
    "--biz-badge-border": hexToRgba(accent, 0.28),
  } as React.CSSProperties;

  const collectionEnabled = businessConfig.enableCollection?.isEnabled !== false;
  // Placeholder for a future business-config field (e.g.
  // businessConfig.enableDineInOrdering) that doesn't exist on the backend
  // yet — hardcoded on so "Add to dine-in session" works for every business
  // until that field ships. Swap this line for the real config read once it does.
  const dineInOrderingEnabled = true;
  // The global active-session marker only turns into dine-in ordering mode
  // on the Products tab when it actually matches the business being viewed —
  // otherwise a session started at Table 4 would leak "Add to dine-in
  // session" onto some other restaurant's page entirely.
  const activeDineInSession = useSelector(selectActiveDineInSession);
  const dineInSessionForThisBusiness =
    dineInOrderingEnabled && activeDineInSession?.businessId === businessId
      ? activeDineInSession
      : null;
  // Gallery sections are opt-in via business config (default off).
  const gallerySectionsEnabled = businessConfig.enableGallerySections?.isEnabled === true;
  // Default false → first product section stays expanded; true → all collapsed.
  const collapseAllProductSections = businessConfig.collapseAllProductSections?.isEnabled === true;
  // NineFinds Orders tab — global flag + either ordering mode enabled by the owner.
  const ordersEnabled =
    AppConfig.featureFlags.enableNineFindsOrders &&
    (businessConfig.enableOnlineOrdering?.isEnabled === true ||
      businessConfig.enableOfflineOrdering?.isEnabled === true);

  // Dine-in kitchen board — STAFF ONLY, unlike Orders. It exposes every live
  // table's code and running bill across the whole floor, which is exactly
  // what a customer sitting at one of those tables must not be handed. The
  // owner/collaborator check is therefore part of the gate itself, not just a
  // canManage prop passed down to the tab.
  const dineInBoardEnabled =
    AppConfig.featureFlags.enableDineInBoard &&
    businessConfig.enableOfflineOrdering?.isEnabled === true &&
    (isOwner || isCollaborator);

  // If a deep-link opened the Orders tab but ordering isn't enabled here, fall back.
  useEffect(() => {
    if (activeTab === "orders" && business && !ordersEnabled) setActiveTab("products");
  }, [activeTab, ordersEnabled, business]);

  // Same fallback for the board. Matters more here than for Orders: a shared
  // ?tab=dinein&table=4 link opened by a customer must land on Products, not
  // on a half-rendered floor view.
  useEffect(() => {
    if (activeTab === "dinein" && business && !dineInBoardEnabled) setActiveTab("products");
  }, [activeTab, dineInBoardEnabled, business]);

  const productCardConfig = {
    isEditIconNeeded: isEditable,
    isDeleteIconNeeded: isEditable,
    isViewIconNeeded: isEditable,
    isDistanceNeeded: false,
    isAddToCartEnabled: collectionEnabled,
    // Business Config → "Showcase product images" (default on). Only the
    // Business page passes this — every other ProductCard consumer omits it
    // and keeps showing real photos/fallback as before.
    showImages: businessConfig.showcaseProductImages?.isEnabled !== false,
    isHideIconNeeded: isEditable,
    isMarkSoldNeeded: isEditable,
    isRevampIconNeeded: isEditable,
    isVoiceEditNeeded: isEditable && voiceAvailable,
    isBusinessPage: true,
  };

  // Patches this one product in place — mirrors the delete/edit callbacks
  // just below (local `products` state, no Redux round-trip).
  const onProductVisibilityChanged = (productId: string, showHide: boolean) => {
    setProducts((prev) =>
      prev.map((p) => (p.productId === productId ? { ...p, showHide } : p))
    );
  };

  // Same in-place patch — a SOLD listing stays in the list (greyed + badge).
  const onProductListingStatusChanged = (productId: string, listingStatus: string) => {
    setProducts((prev) =>
      prev.map((p) => (p.productId === productId ? { ...p, listingStatus } : p))
    );
  };

  // Same in-place patch, for a successful AI image-revamp Accept & Swap —
  // see MyListings.tsx's onProductMediaUpdated for why overwriting
  // `mediaResources` directly is enough (rawMediaResourcesOf checks it first).
  // Followed by a silent background reload() so the list converges on the
  // backend's canonical media regardless (same belt-and-suspenders pattern
  // MyListings uses).
  const onProductMediaUpdated = (productId: string, mediaResources: MediaRecord[]) => {
    setProducts((prev) =>
      prev.map((p) => (p.productId === productId ? { ...p, mediaResources } : p))
    );
    reload();
  };

  // Find the full business object for the edit modal (needed for pre-fill)
  const editableBusinessObj: BusinessProfile | null =
    business ?? myBusinesses.find((b) => b.businessId === businessId) ?? null;

  // Owner sign-off modal, shared by the normal page (the "Review it" strip) and
  // the "Subscription required" screen — an unsubscribed owner never reaches the
  // normal page, so that screen is where an unreviewed business gets reviewed
  // before autopay unlocks.
  const renderConfirmOnboardingModal = () =>
    canManageSubscription && business ? (
      <ConfirmOnboardingModal
        isOpen={isConfirmOnboardingOpen}
        businessId={business.businessId}
        businessName={business.name}
        onClose={() => setIsConfirmOnboardingOpen(false)}
        // BUG FIX (was `onConfirmed={reload}`): `reload()` only refetches the
        // product LISTINGS (fetchBusinessListings), never the business object
        // itself — `isOnboarded` lives on `business` (Redux currentBusiness,
        // populated by fetchBusinessById), so the strip never cleared no
        // matter what the server returned. The modal already gets the
        // updated BusinessDTO back from mark-onboarded, so write it straight
        // into Redux (no extra round trip); fall back to a real refetch only
        // if the server ever answers with an empty envelope. myBusinesses is
        // synced too: the autopay gate reads it on the Subscriptions page.
        onConfirmed={(updatedBusiness) => {
          if (updatedBusiness) {
            dispatch(setCurrentBusiness(updatedBusiness));
            dispatch(syncMyBusiness(updatedBusiness));
          } else {
            dispatch(fetchBusinessById(businessId));
          }
        }}
        // Sign-off is what unlocks autopay, so offer it right away — but only
        // where this business actually needs it (not already paying) and
        // payments can run (not the iOS wrapper).
        offerAutopay={!business.subscriptionActive && !isIosWebView()}
        onSetupAutopay={() => {
          subscribe(business.businessId);
        }}
      />
    ) : null;

  const visitingCardDefaults = useMemo(() => resolveVisitingCardDefaults(business), [business]);
  const fallbackState = resolveBusinessFallbackState(isBusinessLoading, business, canManageSubscription);

  if (fallbackState === "loading") {
    return (
      <div className="biz-listings-page">
        <div className="biz-listings-info-state">
          <div className="biz-listings-loading-spinner" />
        </div>
      </div>
    );
  }

  if (fallbackState === "not-found") {
    return (
      <BusinessInfoState
        icon={<Store size={28} />}
        title="Business not found"
        description="This listing may have been removed, or the link is incorrect."
        primaryLabel="Go back"
        onPrimary={handleBack}
      />
    );
  }

  if (fallbackState === "not-subscribed-visitor") {
    return (
      <BusinessInfoState
        icon={<Lock size={26} />}
        title="This business is not subscribed yet"
        description="The owner hasn't completed their subscription, so this listing isn't visible yet."
        primaryLabel="Go back"
        onPrimary={handleBack}
      />
    );
  }

  if (fallbackState === "not-subscribed-owner" && business) {
    const subscribing = isSubscribing(business.businessId);
    // Autopay unlocks only after the owner signs off the page. Still being set
    // up → nothing to do yet; handed over → the primary action is the review.
    const readiness = getAutopayReadiness(business);
    if (readiness === "in_progress") {
      return (
        <BusinessInfoState
          icon={<Hourglass size={26} />}
          tone="warning"
          title={ONBOARDING_IN_PROGRESS_LABEL}
          description={ONBOARDING_IN_PROGRESS_NOTE}
          primaryLabel="Go back"
          onPrimary={handleBack}
        />
      );
    }
    const reviewPending = readiness === "review";
    return (
      <>
        <BusinessInfoState
          icon={reviewPending ? <ClipboardCheck size={26} /> : <CreditCard size={26} />}
          tone="warning"
          title={reviewPending ? "Review your page first" : "Subscription required"}
          description={
            reviewPending
              ? `Have a look through ${business.name}, confirm it's how you want it, and then you can set up auto-pay.`
              : `Finish setting up auto-pay to make ${business.name} visible to customers again.`
          }
          errorText={reviewPending ? null : getSubscribeError(business.businessId)}
          primaryLabel={
            reviewPending
              ? "Review your page"
              : subscribing ? "Setting up..." : "Start Autopay Setup"
          }
          onPrimary={() =>
            reviewPending
              ? setIsConfirmOnboardingOpen(true)
              : subscribe(business.businessId)
          }
          primaryDisabled={!reviewPending && subscribing}
          secondaryLabel="Manage Subscription"
          onSecondary={() => navigate("/my-profile?tab=subscriptions")}
          tertiaryLabel="Go back"
          onTertiary={handleBack}
        />
        {renderConfirmOnboardingModal()}
      </>
    );
  }

  return (
    <div className="biz-listings-page" ref={pageRef} style={bizStyleVars}>
      <ShareProfileModal
        isOpen={isShareOpen}
        onClose={() => setIsShareOpen(false)}
        profileUrl={`${window.location.origin}${buildBusinessPath(businessId, business?.name)}`}
        userName={business?.name ?? ""}
        title={business?.name ?? ""}
        subtitle="Check out this business on NineFinds"
      />

      <ReportContentModal
        isOpen={isReportOpen}
        contentType="business"
        targetName={business?.name ?? "this business"}
        onClose={() => setIsReportOpen(false)}
      />

      {(isOwner || isCollaborator) && (
        <CustomersModal
          isOpen={isCustomersOpen}
          onClose={() => setIsCustomersOpen(false)}
          businessId={businessId}
          businessName={business?.name}
        />
      )}

      {isEditable && (
        <BusinessConfigEditorModal
          isOpen={isConfigEditorOpen}
          businessId={businessId}
          onClose={() => setIsConfigEditorOpen(false)}
        />
      )}

      <ConfirmModal
        isOpen={pendingGroupDelete !== null}
        title="Delete these products?"
        message={
          pendingGroupDelete
            ? `Delete all ${pendingGroupDelete.productIds.length} product${pendingGroupDelete.productIds.length === 1 ? "" : "s"
            } in "${pendingGroupDelete.label}"? This can't be undone.`
            : ""
        }
        confirmLabel="Delete all"
        tone="danger"
        isLoading={isDeletingGroup}
        onConfirm={handleConfirmGroupDelete}
        onCancel={() => setPendingGroupDelete(null)}
      />

      {(isOwner || isAdmin) && (
        <CreateBusinessModal
          isOpen={isEditBizOpen}
          onClose={() => setIsEditBizOpen(false)}
          editBusiness={editableBusinessObj}
        />
      )}

      {renderConfirmOnboardingModal()}

      {/* Create product modal — launched from this business page with business pre-locked */}
      {(isOwner || isCollaborator) && (
        <Suspense fallback={null}>
          <CreateProduct
            isOpen={createOpen}
            startWithVoice={startVoice}
            lockBusinessId={business?.businessId}
            voice={voiceProduct ?? undefined}
            onClose={() => { setCreateOpen(false); setStartVoice(false); setVoiceProduct(null); dispatch(resetToInitialState()); }}
            onProductUpdate={async (id) => {
              try {
                const updatedProduct = await ProductApiService.getProductDetailsById(id);
                setProducts((prev) => {
                  const index = prev.findIndex((p) => p.productId === id);
                  if (index === -1) return [updatedProduct, ...prev];
                  const next = [...prev];
                  next[index] = updatedProduct;
                  return next;
                });
              } catch (e) {
                console.error("Failed to fetch single product, reloading full list", e);
                setProducts((prev) => prev.filter((p) => p.productId !== id));
                reload();
              }
            }}
          />
        </Suspense>
      )}

      {/* Bulk upload modal — curator or platform admin, not gated on
          isEditable. Unlike onDeleteGroup below, curator-ship (or admin
          status) is independent of being a collaborator on this specific
          business (see NineSetupBulkActions, which lets a curator
          bulk-upload to a business they're building out without ever being
          added to its collaborators) — requiring isEditable here would hide
          this from the exact curator who built the page. Bulk upload is
          additive, unlike the destructive delete-all below, so it doesn't
          need that extra restriction. */}
      {(isCurator || isAdmin) && business && (
        <Suspense fallback={null}>
          <BusinessBulkUploadActions
            isOpen={isBulkUploadOpen}
            business={business}
            onClose={() => setIsBulkUploadOpen(false)}
            onUploaded={reload}
          />
        </Suspense>
      )}

      {/* ── Header ── */}
      <div className="biz-listings-header">
        {/* Hero banner — the owner's own image if set, otherwise a gradient
            generated from this business's accent color. Every business gets
            a distinct hero even with zero setup. */}
        <div
          ref={bannerRef}
          className={`biz-listings-banner ${activeBannerUrl ? "biz-listings-banner--photo" : "biz-listings-banner--generated"
            }`}
          style={
            activeBannerUrl
              ? pullStretchStyle
              : { backgroundImage: `linear-gradient(135deg, ${accent} 0%, ${accentDark} 100%)` }
          }
        >
          {activeBannerUrl && (
            <img
              key={activeBannerUrl}
              src={activeBannerUrl}
              alt=""
              className="biz-listings-banner-img"
              onError={() =>
                setFailedBannerUrls((prev) => new Set(prev).add(activeBannerUrl))
              }
            />
          )}
          <div
            className={`biz-listings-banner-scrim ${activeBannerUrl ? "biz-listings-banner-scrim--photo" : ""}`}
            // Fades slightly as the user pulls further — the revealed photo
            // reads as a little reward for the gesture instead of staying
            // uniformly dimmed the whole time.
            style={activeBannerUrl ? { opacity: 1 - bannerPullRatio * 0.4 } : undefined}
          />

          <div className="biz-listings-topbar biz-listings-topbar--overlay biz-listings-topbar--fixed">
            <div className="biz-listings-topbar-left">
              <button
                className="biz-listings-back-btn"
                onClick={handleBack}
                aria-label="Go back"
              >
                <ArrowLeft size={20} />
              </button>

              {/* Compact title — fades in once the large name above has
                  scrolled under the bar, so there's still a "you're on
                  <business>" anchor once the hero is out of view. Purely
                  decorative (aria-hidden): the real, accessible heading is
                  the <h1> in the identity block below. */}
              <div className="biz-listings-topbar-title" aria-hidden="true">
                <span className="biz-listings-topbar-title-logo">
                  {logoUrl ? (
                    <img src={logoUrl} alt="" onError={() => setLogoFailed(true)} />
                  ) : (
                    <BusinessMonogramAvatar name={business?.name ?? "?"} accent={accent} size={22} />
                  )}
                </span>
                <span className="biz-listings-topbar-title-name">{business?.name ?? ""}</span>
              </div>
            </div>

            <div className="biz-listings-topbar-actions">
              {isAdmin && (
                <button
                  className="biz-listings-action-btn"
                  onClick={() => setIsEditBizOpen(true)}
                  aria-label="Edit business"
                  title="Edit business"
                >
                  <Pencil size={18} />
                </button>
              )}
              {(isOwner || isCollaborator) && (
                <button
                  className="biz-listings-action-btn"
                  onClick={() => setIsCustomersOpen(true)}
                  aria-label="Customers"
                  title="Customers"
                >
                  <Users size={18} />
                </button>
              )}
              {isEditable && (
                <button
                  className="biz-listings-action-btn"
                  onClick={() => setIsConfigEditorOpen(true)}
                  aria-label="Business config"
                  title="Business config"
                >
                  <Settings2 size={18} />
                </button>
              )}
              <button
                className="biz-listings-action-btn"
                onClick={() => setIsShareOpen(true)}
                aria-label="Share"
              >
                <Share2 size={18} />
              </button>

              {canReportBusiness && (
                <button
                  className="biz-listings-action-btn"
                  onClick={() => setIsReportOpen(true)}
                  aria-label="Report business"
                  title="Report business"
                >
                  <Flag size={18} />
                </button>
              )}
            </div>
          </div>

          {/* Owner/collaborator (or platform admin) upload affordance —
              bottom-right of the banner so it doesn't collide with the
              back/share buttons. */}
          {canEditMedia && (
            <label
              className={`biz-listings-banner-upload ${isBannerUploading ? "is-uploading" : ""}`}
              title="Change banner image"
            >
              {isBannerUploading ? (
                <Loader2 size={14} className="biz-logo-spinner" />
              ) : (
                <Camera size={14} />
              )}
              <span>Change banner</span>
              <input
                type="file"
                accept="image/*"
                className="biz-listings-banner-upload-input"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  e.target.value = "";
                  if (file) {
                    const ALLOWED_TYPES = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
                    const MAX_SIZE_MB = 5;
                    if (!ALLOWED_TYPES.includes(file.type)) {
                      triggerNotification("Please select a JPEG, PNG, or WEBP image.", NotificationType.ERROR);
                      return;
                    }
                    if (file.size > MAX_SIZE_MB * 1024 * 1024) {
                      triggerNotification(`Image must be smaller than ${MAX_SIZE_MB}MB.`, NotificationType.ERROR);
                      return;
                    }
                    setBannerCropSrc(URL.createObjectURL(file));
                  }
                }}
              />
            </label>
          )}
        </div>

        {/* Business identity */}
        <div className="biz-listings-identity biz-listings-identity--overlap">
          <div className="biz-listings-logo">
            {canEditMedia ? (
              <BusinessLogoUploader
                variant="compact"
                existingLogoUrl={logoUrl}
                onFileSelected={handleLogoFileSelected}
                onInvalidFile={(reason) => triggerNotification(reason, NotificationType.ERROR)}
                isUploading={isLogoUploading}
              />
            ) : logoUrl ? (
              <img
                src={logoUrl}
                alt={business?.name ?? ""}
                className="biz-listings-logo-img"
                onError={() => setLogoFailed(true)}
              />
            ) : (
              <BusinessMonogramAvatar name={business?.name ?? "?"} accent={accent} />
            )}
          </div>
          <div className="biz-listings-identity-info">
            <h1 className="biz-listings-name" style={{ fontFamily: fontOption.stack }}>
              {business?.name ?? "—"}
            </h1>
            {business?.description && (
              <ExpandableText className="biz-listings-description" text={business.description} />
            )}
            <div className="biz-listings-meta-row">
              {business?.listingType && (
                <span className="biz-listings-type-badge">
                  <Sparkles size={13} fill="currentColor" />
                  {CreateProductConfigHelper.listingTypeIdToLabel(business.listingType)}
                </span>
              )}
              <BusinessLiveBadge hours={business?.moreDetails?.hours as BusinessHours | undefined} />
              {ratingsSummary && (
                <BusinessRatingPill
                  avgRating={ratingsSummary.avgRating}
                  totalRatings={ratingsSummary.totalRatings}
                />
              )}
              {isCollaborator && (
                <span className="biz-listings-collaborator-chip">
                  <Users size={13} />
                  Collaborating
                </span>
              )}
              {business?.createdAt && !Number.isNaN(new Date(business.createdAt).getTime()) && (
                <span className="biz-listings-tenure-badge" title={`On NineFinds since ${new Date(business.createdAt).getFullYear()}`}>
                  Since {new Date(business.createdAt).getFullYear()}
                </span>
              )}
              {/* TODO(biz-listings-dine-in-badge): temporarily hidden for EVERYONE.
                  Remove SHOW_DINE_IN_BADGE (and this gate) later so the badge shows
                  again for dine-in enabled businesses only (dineInOrderingEnabled). */}
              {SHOW_DINE_IN_BADGE && dineInOrderingEnabled && (
                <span className="biz-listings-dine-in-badge" style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "4px",
                  padding: "2px 8px",
                  borderRadius: "12px",
                  backgroundColor: "#3b82f6",
                  color: "#ffffff",
                  fontSize: "12px",
                  fontWeight: 600,
                  border: "1px solid #2563eb",
                  boxShadow: "0 2px 4px rgba(59, 130, 246, 0.3)"
                }}>
                  <CookingPot size={14} strokeWidth={2} />
                  Dine-in
                </span>
              )}
            </div>

          </div>
        </div>

        <div className="biz-contact-stats-container">
          <div className="biz-contact-stats">
            {business && (business.ownerId || business.creatorId) && (
              <Link
                className="biz-contact-stat"
                to={`/listings/${business.ownerId ?? business.creatorId}`}
                title={`View ${business.ownerId ? business.ownerName ?? "Owner" : business.creatorName ?? "Manager"}'s profile`}
              >
                <div className="biz-contact-stat__top" style={{ color: 'var(--biz-n-600)' }}>
                  <div style={{ width: 14, height: 14, borderRadius: '50%', background: '#8b5cf6', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '8px', fontWeight: 'bold' }}>
                    {business.ownerId ? (business.ownerName || "O").substring(0, 2).toUpperCase() : (business.creatorName || "M").substring(0, 2).toUpperCase()}
                  </div>
                  {business.ownerId ? "Owned by" : "Managed by"}
                </div>
                <div className="biz-contact-stat__value">
                  {business.ownerId ? business.ownerName ?? "Owner" : business.creatorName ?? "Manager"}
                </div>
                <div className="biz-contact-stat__bottom">
                  View profile <ArrowUpRight size={12} style={{ marginLeft: 2 }} />
                </div>
              </Link>
            )}
            {business?.phone && business?.listingType !== "real_estate" && (
              <button
                type="button"
                className="biz-contact-stat"
                onClick={() => handleCopyPhone(business.phone)}
                title="Copy phone number"
                aria-label={`Copy phone number ${business.phone}`}
              >
                <div className="biz-contact-stat__top" style={{ color: '#2563eb' }}>
                  <Phone size={14} /> Call us
                </div>
                <div className="biz-contact-stat__value">{business.phone}</div>
                <div className="biz-contact-stat__bottom">
                  {isPhoneCopied ? (
                    <>
                      <Check size={12} color="#16a34a" /> Copied!
                    </>
                  ) : (
                    <>
                      Tap to copy <Copy size={12} style={{ marginLeft: 2 }} />
                    </>
                  )}
                </div>
              </button>
            )}
            <BusinessExternalLinks businessId={businessId} location={business?.moreDetails?.location} socials={business?.moreDetails?.socials} />
          </div>
        </div>

        {/* Owner sign-off prompt — only for a page built through the Nine Setup
            pipeline and not yet confirmed.

            `=== false` is load-bearing: null means this business never went
            through that pipeline, so a truthy check would nag every owner in
            the app. Owner-only — a collaborator can't sign off work on someone
            else's behalf.

            Sits under the identity block rather than over the banner: it's an
            invitation, not an alert, and the owner should see their finished
            page before being asked whether they like it. */}
        {isOwner && business?.isOnboarded === false && (
          <div className="biz-onboarding-strip">
            <div className="biz-onboarding-strip-text">
              <strong>Your page is ready</strong>
              <span>Set up by our captain and curator — have a look through.</span>
            </div>
            <button
              type="button"
              className="biz-onboarding-strip-btn"
              onClick={() => setIsConfirmOnboardingOpen(true)}
            >
              <PartyPopper size={15} aria-hidden="true" />
              Review it
            </button>
          </div>
        )}






      </div>

      {/* Small persistent reminder that this page is being browsed
          mid dine-in-session — explains why the Products tab's floating
          button says "Add to dine-in session" instead of "View Collection".
          Deliberately NOT sticky/floating and placed BEFORE the scroll
          anchor below: it must stay out of the ?tab= scroll target so
          "View menu" still lands the sticky tab bar flush at the top,
          exactly as fixed above. */}
      {dineInSessionForThisBusiness && (
        <button
          type="button"
          className="biz-listings-dinein-banner"
          onClick={() =>
            navigate(
              `/dine-in/${dineInSessionForThisBusiness.businessId}/${dineInSessionForThisBusiness.tableNumber}`
            )
          }
        >
          <UtensilsCrossed size={14} strokeWidth={2} />
          <span>Ordering for Table {dineInSessionForThisBusiness.tableNumber}</span>
        </button>
      )}

      {/* Zero-height scroll anchor for ?tab= deep links. Deliberately a
          SIBLING of the tab bar, not a wrapper: .biz-tabbar is sticky, and a
          sticky element can only stick within its parent's box — wrapping it
          in a div of its own height leaves it zero room to stick, silently
          turning it back into a normal scrolling element. */}
      <div ref={tabBarAnchorRef} aria-hidden="true" />
      <BusinessTabBar
        activeTab={activeTab}
        onTabChange={setActiveTab}
        showOrders={ordersEnabled}
        showDineIn={dineInBoardEnabled}
      />
      {/* Each tab is its own chunk: the business profile is the most common
          QR landing page, and a visitor should not download the gallery
          (pdf.js) or the drag-and-drop product editor to read one tab. */}
      <div className="biz-tabs-content">
        <Suspense fallback={<div className="biz-tab-loading" aria-busy="true" />}>
          {activeTab === "products" && (
            error && !isLoading && products.length === 0 ? (
              <FetchErrorState
                message={`Couldn't load listings: ${error}`}
                onRetry={reload}
                autoRetrySeconds={30}
              />
            ) : !isLoading && products.length === 0 ? (
              <div className="biz-listings-empty">
                <div className="biz-listings-empty-icon" aria-hidden="true">
                  <Package size={28} strokeWidth={1.75} />
                </div>
                <h5>
                  {(isOwner || isCollaborator)
                    ? "No listings yet — add your first one!"
                    : "No listings yet."}
                </h5>
                <div className="biz-listings-empty-actions">
                  {(isOwner || isCollaborator) && (
                    <button
                      className="biz-listings-empty-btn biz-listings-empty-btn--primary"
                      onClick={handleSellFromBusiness}
                    >
                      + Add a Listing
                    </button>
                  )}
                  {(isCurator || isAdmin) && (
                    <button
                      className="biz-listings-empty-btn biz-listings-empty-btn--secondary"
                      onClick={() => setIsBulkUploadOpen(true)}
                    >
                      Bulk upload
                    </button>
                  )}
                </div>
              </div>
            ) : (
              <ProductsTab
                categoryAisles
                products={products}
                totalCount={totalCount}
                productCardConfig={productCardConfig}
                onLoadMore={loadMore}
                onProductDeleted={(id) =>
                  setProducts((prev) => prev.filter((p) => p.productId !== id))
                }
                onProductEdit={isEditable ? handleProductEdit : undefined}
                onProductVoiceEdit={isEditable && voiceAvailable ? handleProductVoiceEdit : undefined}
                onVisibilityChanged={isEditable ? onProductVisibilityChanged : undefined}
                onListingStatusChanged={isEditable ? onProductListingStatusChanged : undefined}
                onMediaUpdated={isEditable ? onProductMediaUpdated : undefined}
                isOwner={isOwner}
                sectionsEnabled={sectionsEnabled}
                collapseAllSections={collapseAllProductSections}
                onToggleSections={handleToggleSections}
                onSell={isEditable ? handleSellFromBusiness : undefined}
                onTellChintu={isEditable && voiceCreateAvailable ? handleTellChintu : undefined}
                isLoading={isLoading}
                activeDineInSession={dineInSessionForThisBusiness}
                onDeleteGroup={isEditable && isCurator ? handleDeleteGroup : undefined}
                onBulkUpload={isCurator || isAdmin ? () => setIsBulkUploadOpen(true) : undefined}
              />
            )
          )}
          {activeTab === "gallery" && (
            <GalleryTab
              businessId={businessId}
              isEditable={isEditable || isAdmin}
              sectionsEnabled={gallerySectionsEnabled}
              businessName={business?.name}
              logoUrl={logoUrl}
              brandAccentColor={pastelHex(accent)}
              listingType={business?.listingType}
              cardDefaults={visitingCardDefaults}
            />
          )}
          {activeTab === "reviews" && (
            <ReviewsTab businessId={businessId} currentUserId={currentUserId} />
          )}
          {activeTab === "more" && (
            <MoreTab
              details={business?.moreDetails}
              businessId={businessId}
              isEditable={canEditMoreDetails}
            />
          )}
          {activeTab === "orders" && ordersEnabled && (
            <OrdersTab
              businessId={businessId}
              currentUserId={currentUserId}
              canManage={isOwner || isCollaborator}
              businessName={business?.name}
              businessAddress={business?.address}
            />
          )}
        </Suspense>
        {activeTab === "dinein" && dineInBoardEnabled && (
          <DineInTab
            businessId={businessId}
            businessName={business?.name ?? ""}
            businessLogoUrl={logoUrl || undefined}
            tableCount={businessConfig.dineInTableCount?.value}
          />
        )}
      </div>

      {isEditBizOpen && business && (
        <CreateBusinessModal
          isOpen={isEditBizOpen}
          onClose={() => setIsEditBizOpen(false)}
          editBusiness={business}
        />
      )}

      {bannerCropSrc && (
        <ImageCropModal
          imageSrc={bannerCropSrc}
          aspectRatio={3}
          onCropComplete={(croppedBlob) => {
            const file = new File([croppedBlob], "banner.jpg", { type: "image/jpeg" });
            handleBannerFileSelected(file);
            if (bannerCropSrc) URL.revokeObjectURL(bannerCropSrc);
            setBannerCropSrc(null);
          }}
          onCancel={() => {
            if (bannerCropSrc) URL.revokeObjectURL(bannerCropSrc);
            setBannerCropSrc(null);
          }}
        />
      )}
    </div>
  );
};

export default BusinessListings;